import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <div className="container">
        <h1>Politique de Confidentialité</h1>
        <p className="last-updated">Dernière mise à jour : 01/12/2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Shini Pay ("nous", "notre", "l'application") s'engage à protéger votre vie privée. 
            Cette politique de confidentialité explique comment l'application mobile fonctionne concernant vos données.
          </p>
          <p>
            <strong>Point important :</strong> L'application mobile Shini Pay ne collecte aucune donnée personnelle. 
            L'application agit uniquement comme une interface pour accéder aux services du backend, qui gère toutes les données personnelles. 
            Cette politique décrit uniquement les pratiques de l'application mobile elle-même.
          </p>
          <p>
            En utilisant Shini Pay, vous acceptez les pratiques décrites dans cette politique de confidentialité. 
            Si vous n'acceptez pas cette politique, veuillez ne pas utiliser notre application.
          </p>
        </section>

        <section>
          <h2>2. Collecte de données</h2>
          <p>
            <strong>Shini Pay ne collecte pas de données personnelles.</strong> Toutes les données nécessaires au fonctionnement 
            de l'application sont gérées par le backend auquel l'application se connecte. L'application mobile elle-même ne stocke 
            et ne transmet aucune donnée personnelle à nos serveurs.
          </p>
          <h3>2.1. Données stockées localement</h3>
          <p>L'application stocke uniquement sur votre appareil :</p>
          <ul>
            <li><strong>Token d'authentification :</strong> pour maintenir votre session de connexion (stocké de manière sécurisée)</li>
            <li><strong>Préférences biométriques :</strong> paramètre d'activation/désactivation de l'authentification biométrique (stocké localement uniquement)</li>
          </ul>
          <p>
            <strong>Note importante :</strong> Les données biométriques (empreintes digitales, Face ID) sont gérées exclusivement 
            par le système d'exploitation de votre appareil et ne sont jamais accessibles à l'application ni transmises à nos serveurs.
          </p>
          <h3>2.2. Données gérées par le backend</h3>
          <p>
            Les données personnelles (nom, prénom, numéro de téléphone, email, comptes, transactions) sont gérées et stockées 
            par le système backend auquel l'application se connecte. Ces données ne sont pas collectées ni stockées par l'application mobile Shini Pay.
          </p>
        </section>

        <section>
          <h2>3. Utilisation des données</h2>
          <p>
            Comme Shini Pay ne collecte pas de données personnelles, l'application n'utilise aucune donnée personnelle. 
            L'application agit uniquement comme une interface pour accéder aux services du backend.
          </p>
          <p>Les données stockées localement sont utilisées uniquement pour :</p>
          <ul>
            <li><strong>Authentification :</strong> maintenir votre session de connexion via le token d'authentification</li>
            <li><strong>Sécurité :</strong> permettre l'authentification biométrique si vous l'avez activée</li>
          </ul>
          <p>
            Toutes les autres fonctionnalités (transactions, gestion de compte, etc.) sont gérées directement par le backend 
            auquel l'application se connecte, et non par l'application mobile elle-même.
          </p>
        </section>

        <section>
          <h2>4. Partage de données</h2>
          <p>
            <strong>Shini Pay ne partage aucune donnée personnelle</strong> car l'application ne collecte pas de données personnelles. 
            L'application ne fait que transmettre les requêtes de l'utilisateur au backend, qui gère toutes les données personnelles.
          </p>
          <p>
            Concernant les données gérées par le backend (qui sont indépendantes de l'application mobile Shini Pay), 
            veuillez consulter la politique de confidentialité du service backend pour plus d'informations sur le partage de ces données.
          </p>
        </section>

        <section>
          <h2>5. Sécurité</h2>
          <p>
            Bien que Shini Pay ne collecte pas de données personnelles, nous mettons en œuvre des mesures de sécurité 
            pour protéger les données minimales stockées localement :
          </p>
          <ul>
            <li><strong>Chiffrement des communications :</strong> toutes les communications avec le backend sont chiffrées en transit (HTTPS/TLS)</li>
            <li><strong>Stockage sécurisé :</strong> le token d'authentification est stocké de manière sécurisée sur votre appareil (flutter_secure_storage)</li>
            <li><strong>Authentification biométrique :</strong> protection de l'accès à l'application par empreinte digitale ou Face ID (gérée par le système d'exploitation)</li>
            <li><strong>Aucune collecte :</strong> l'application ne collecte ni ne transmet aucune donnée personnelle à nos serveurs</li>
          </ul>
          <p>
            Les données personnelles gérées par le backend sont protégées selon les mesures de sécurité du service backend. 
            Pour plus d'informations sur la sécurité des données gérées par le backend, veuillez consulter la politique de sécurité du backend.
          </p>
        </section>

        <section>
          <h2>6. Conservation des données</h2>
          <p>
            <strong>Shini Pay ne conserve aucune donnée personnelle.</strong> Les seules données stockées localement sur votre appareil sont :
          </p>
          <ul>
            <li><strong>Token d'authentification :</strong> conservé jusqu'à ce que vous vous déconnectiez ou que vous supprimiez l'application</li>
            <li><strong>Préférences biométriques :</strong> conservées jusqu'à ce que vous les modifiiez ou supprimiez l'application</li>
          </ul>
          <p>
            Vous pouvez supprimer toutes les données locales de l'application en la désinstallant de votre appareil. 
            Les données personnelles gérées par le backend sont conservées selon la politique du service backend.
          </p>
        </section>

        <section>
          <h2>7. Vos droits</h2>
          <p>
            Comme Shini Pay ne collecte pas de données personnelles, vous avez un contrôle total sur les données stockées localement :
          </p>
          <ul>
            <li><strong>Suppression des données locales :</strong> vous pouvez supprimer toutes les données locales en désinstallant l'application</li>
            <li><strong>Désactivation de la biométrie :</strong> vous pouvez activer ou désactiver l'authentification biométrique à tout moment dans les paramètres de l'application</li>
            <li><strong>Déconnexion :</strong> vous pouvez vous déconnecter à tout moment, ce qui supprimera le token d'authentification stocké localement</li>
          </ul>
          <p>
            Pour exercer vos droits concernant les données personnelles gérées par le backend, veuillez contacter le service backend 
            ou nous contacter à l'adresse : <a href="mailto:cheickm.sissoko@djammadev.com">cheickm.sissoko@djammadev.com</a>
          </p>
        </section>

        <section>
          <h2>8. Authentification biométrique</h2>
          <p>
            Shini Pay propose une option d'authentification biométrique (empreintes digitales, Face ID) pour sécuriser l'accès à l'application. 
            Cette fonctionnalité est entièrement optionnelle et peut être activée ou désactivée à tout moment.
          </p>
          <p>
            <strong>Important :</strong> Les données biométriques sont gérées exclusivement par le système d'exploitation de votre appareil 
            (iOS ou Android). L'application Shini Pay n'a jamais accès à ces données biométriques et elles ne sont jamais transmises à nos serveurs. 
            Seul le résultat de l'authentification (succès/échec) est utilisé par l'application pour déverrouiller l'accès.
          </p>
        </section>

        <section>
          <h2>9. Cookies et technologies similaires</h2>
          <p>
            Shini Pay est une application mobile native et n'utilise pas de cookies. L'application stocke uniquement les données 
            minimales nécessaires au fonctionnement (token d'authentification et préférences biométriques) directement sur votre appareil, 
            sans utiliser de technologies de suivi.
          </p>
        </section>

        <section>
          <h2>10. Services tiers</h2>
          <p>
            Shini Pay peut vous rediriger vers des services de paiement tiers (Orange Money, Sama Money, Stripe) pour effectuer des transactions. 
            Ces redirections sont gérées par le backend auquel l'application se connecte.
          </p>
          <p>
            <strong>Important :</strong> L'application mobile Shini Pay ne collecte aucune donnée lors de ces redirections. 
            Toutes les interactions avec les services tiers sont gérées directement par le backend. 
            Nous vous encourageons à lire les politiques de confidentialité de ces services tiers pour comprendre comment ils gèrent vos données.
          </p>
        </section>

        <section>
          <h2>11. Modifications de cette politique</h2>
          <p>
            Nous pouvons modifier cette politique de confidentialité de temps à autre. 
            Nous vous informerons de tout changement significatif en publiant la nouvelle politique sur cette page 
            et en mettant à jour la date de "Dernière mise à jour". 
            Nous vous encourageons à consulter régulièrement cette page pour rester informé.
          </p>
        </section>

        <section>
          <h2>12. Protection des mineurs</h2>
          <p>
            Shini Pay n'est pas destiné aux personnes de moins de 18 ans. 
            Comme l'application ne collecte pas de données personnelles, aucune donnée personnelle de mineurs n'est collectée par l'application mobile. 
            Les données personnelles gérées par le backend sont soumises à la politique de confidentialité du service backend.
          </p>
        </section>

        <section>
          <h2>13. Transferts internationaux</h2>
          <p>
            Comme Shini Pay ne collecte pas de données personnelles, aucun transfert international de données personnelles 
            n'est effectué par l'application mobile. Les données minimales stockées localement (token d'authentification, 
            préférences biométriques) restent sur votre appareil et ne sont jamais transférées.
          </p>
          <p>
            Concernant les données gérées par le backend, veuillez consulter la politique de confidentialité du service backend 
            pour plus d'informations sur les transferts internationaux.
          </p>
        </section>

        <section>
          <h2>14. Contact</h2>
          <p>
            Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
            vous pouvez nous contacter :
          </p>
          <ul>
            <li><strong>Email :</strong> <a href="mailto:cheickm.sissoko@djammadev.com">cheickm.sissoko@djammadev.com</a></li>
            <li><strong>Téléphone :</strong> <a href="tel:+22377291138">+223 77 29 11 38</a></li>
            <li><strong>WhatsApp :</strong> <a href="https://wa.me/22377291138" target="_blank" rel="noopener noreferrer">+223 77 29 11 38</a></li>
          </ul>
        </section>

        <section>
          <h2>15. Consentement</h2>
          <p>
            En utilisant Shini Pay, vous reconnaissez avoir lu et compris cette politique de confidentialité. 
            Vous acceptez que l'application stocke localement sur votre appareil uniquement les données minimales nécessaires 
            au fonctionnement (token d'authentification et préférences biométriques), et que ces données ne sont jamais 
            transmises à nos serveurs.
          </p>
          <p>
            Vous reconnaissez également que les données personnelles sont gérées par le backend auquel l'application se connecte, 
            et que ces données sont soumises à la politique de confidentialité du service backend.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

